import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { splitFullName } from "@/lib/name-utils";
import {
  blockedBySaturdayRule,
  isHighSeasonDay,
  makeTurnoverCheck,
} from "@/lib/saturday-rule";

const DateString = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Expected YYYY-MM-DD");

/**
 * Strict deliverable-email guard for outbound mail to renters.
 * Goes beyond Zod's loose `.email()`: enforces single @, a real TLD,
 * no leading/trailing dot, no consecutive dots, sane length and
 * lowercases the address. Returns null if not safe to send.
 */
const StrictDeliverableEmail = z
  .string()
  .trim()
  .toLowerCase()
  .min(6)
  .max(254)
  .email()
  .refine((v) => {
    const parts = v.split("@");
    if (parts.length !== 2) return false;
    const [local, domain] = parts;
    if (!local || !domain) return false;
    if (local.length > 64) return false;
    if (local.startsWith(".") || local.endsWith(".")) return false;
    if (local.includes("..") || domain.includes("..")) return false;
    const labels = domain.split(".");
    if (labels.length < 2) return false;
    const tld = labels[labels.length - 1];
    if (tld.length < 2 || !/^[a-z]{2,}$/.test(tld)) return false;
    return labels.every((l) => /^[a-z0-9-]+$/.test(l) && !l.startsWith("-") && !l.endsWith("-"));
  }, { message: "Email does not look deliverable" });

async function validateRenterEmail(raw: string): Promise<
  | { ok: true; email: string }
  | { ok: false; reason: "invalid" | "suppressed" }
> {
  const parsed = StrictDeliverableEmail.safeParse(raw);
  if (!parsed.success) return { ok: false, reason: "invalid" };
  const email = parsed.data;
  try {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data } = await supabaseAdmin
      .from("suppressed_emails")
      .select("email")
      .eq("email", email)
      .maybeSingle();
    if (data?.email) return { ok: false, reason: "suppressed" };
  } catch (e) {
    // Suppression check failure should not block the send.
    console.warn("[validateRenterEmail] suppression check failed", e);
  }
  return { ok: true, email };
}

const InquirySchema = z.object({
  name: z.string().trim().min(1).max(100),
  firstName: z.string().trim().max(60).optional().default(""),
  lastName: z.string().trim().max(80).optional().default(""),
  email: z.string().trim().email().max(255),
  phone: z.string().trim().min(3).max(40),
  country: z.string().trim().min(1).max(80),
  checkIn: DateString,
  checkOut: DateString,
  bedrooms: z.coerce.number().int().min(1).max(4),
  adults: z.coerce.number().int().min(1).max(8),
  children: z.coerce.number().int().min(0).max(8),
  babies: z.coerce.number().int().min(0).max(2),
  message: z.string().trim().max(2000).optional().default(""),
  earlyCheckIn: z.coerce.boolean().optional().default(false),
  lateCheckOut: z.coerce.boolean().optional().default(false),
  quotedRentalCents: z.coerce.number().int().min(0).max(100_000_00).optional(),
  quotedExtrasCents: z.coerce.number().int().min(0).max(100_000_00).optional(),
  quotedTotalCents: z.coerce.number().int().min(0).max(100_000_00).optional(),
  quotedDiscountCents: z.coerce.number().int().min(0).max(100_000_00).optional(),
  quotedCurrency: z.string().trim().min(2).max(8).optional(),
  quotedBreakdown: z.string().max(4000).optional(),
  lang: z.enum(["nl", "fr", "en", "de"]).optional(),
}).refine((v) => v.adults + v.children + v.babies <= 8, {
  message: "Maximum 8 guests in total (adults + children + babies).",
  path: ["adults"],
});

export type Inquiry = z.infer<typeof InquirySchema>;

export type BookedRange = {
  start: string;
  end: string;
  status: "pending" | "confirmed" | "on_request";
};

export const getBookedRanges = createServerFn({ method: "GET" }).handler(
  async (): Promise<BookedRange[]> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const today = new Date().toISOString().slice(0, 10);
    const { data, error } = await supabaseAdmin
      .from("bookings")
      .select("check_in, check_out, status, on_request")
      .neq("status", "cancelled")
      .gte("check_out", today)
      .order("check_in", { ascending: true });
    if (error) {
      console.error("[getBookedRanges]", error);
      return [];
    }
    return (data ?? []).map((r) => ({
      start: r.check_in as string,
      end: r.check_out as string,
      status: (r as { on_request?: boolean }).on_request
        ? ("on_request" as const)
        : (r.status as string) === "confirmed"
          ? ("confirmed" as const)
          : ("pending" as const),
    }));
  },
);

export const submitBookingInquiry = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => InquirySchema.parse(data))
  .handler(async ({ data }) => {
    if (data.checkOut <= data.checkIn) {
      return {
        ok: false as const,
        conflict: false as const,
        message: "Check-out must be after check-in.",
      };
    }

    // Load admin overrides once; used both for the lead-time bypass and the
    // overlap bypass below.
    let activeOverrides: Array<{ start: string; end: string }> = [];
    try {
      const { supabaseAdmin: adminForOverride } = await import(
        "@/integrations/supabase/client.server"
      );
      const { data: settings } = await adminForOverride
        .from("pricing_settings")
        .select("lead_time_overrides")
        .eq("id", "singleton")
        .maybeSingle();
      const raw = (settings?.lead_time_overrides ?? []) as Array<{
        start: string;
        end: string;
        enabled?: boolean;
      }>;
      activeOverrides = raw
        .filter(
          (r) =>
            typeof r?.start === "string" &&
            typeof r?.end === "string" &&
            r.enabled !== false,
        )
        .map((r) => ({ start: r.start, end: r.end }));
    } catch (e) {
      console.error("[submitBookingInquiry] override lookup failed", e);
    }
    const selectionCoveredByOverride = activeOverrides.some(
      (r) => data.checkIn >= r.start && data.checkOut <= r.end,
    );

    // Require at least 5 days of lead time between today and check-in.
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const minCheckIn = new Date(today);
    minCheckIn.setDate(minCheckIn.getDate() + 5);
    const minCheckInISO = minCheckIn.toISOString().slice(0, 10);
    if (data.checkIn < minCheckInISO) {
      const leadAllowed = activeOverrides.some(
        (r) => data.checkIn >= r.start && data.checkIn < r.end,
      );
      if (!leadAllowed) {
        return {
          ok: false as const,
          conflict: false as const,
          message:
            "Check-in must be at least 5 days from today. Please pick a later arrival date.",
        };
      }
    }

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    // July & August are Saturday-to-Saturday. Turnover days of an existing
    // booking and admin-released periods stay allowed.
    if (
      !selectionCoveredByOverride &&
      (isHighSeasonDay(data.checkIn) || isHighSeasonDay(data.checkOut))
    ) {
      let existing: Array<{ start: string; end: string }> = [];
      try {
        const { data: rows } = await supabaseAdmin
          .from("bookings")
          .select("check_in, check_out")
          .neq("status", "cancelled");
        existing = (rows ?? []).map((r) => ({
          start: r.check_in as string,
          end: r.check_out as string,
        }));
      } catch (e) {
        console.error("[submitBookingInquiry] saturday rule lookup failed", e);
      }
      const isTurnover = makeTurnoverCheck(existing);
      const opts = { overrides: activeOverrides, isTurnover };
      if (
        blockedBySaturdayRule(data.checkIn, opts) ||
        blockedBySaturdayRule(data.checkOut, opts)
      ) {
        return {
          ok: false as const,
          conflict: false as const,
          message:
            "In July and August stays run Saturday to Saturday. Please pick a Saturday arrival and departure.",
        };
      }
    }

    // Overlap rule (half-open intervals): existing.check_in < new.check_out
    //                                AND existing.check_out > new.check_in
    const { data: conflicts, error: conflictErr } = await supabaseAdmin
      .from("bookings")
      .select("id, status, on_request")
      .neq("status", "cancelled")
      .eq("on_request", false)
      .lt("check_in", data.checkOut)
      .gt("check_out", data.checkIn)
      .limit(1);

    if (conflictErr) {
      console.error("[submitBookingInquiry] conflict check failed", conflictErr);
      throw new Error("Could not verify availability. Please try again.");
    }

    const hasConfirmedConflict = (conflicts ?? []).some(
      (c) => (c as { status?: string }).status === "confirmed",
    );
    if (
      conflicts &&
      conflicts.length > 0 &&
      (hasConfirmedConflict || !selectionCoveredByOverride)
    ) {
      return {
        ok: false as const,
        conflict: true as const,
        message:
          "Those dates are no longer available. Please pick a different range on the calendar.",
      };
    }

    const reference =
      "CMP-" +
      Math.random().toString(36).slice(2, 7).toUpperCase() +
      Date.now().toString(36).slice(-4).toUpperCase();

    const { data: inserted, error: insertErr } = await supabaseAdmin.from("bookings").insert({
      reference,
      name: data.name,
      email: data.email,
      phone: data.phone,
      country: data.country,
      check_in: data.checkIn,
      check_out: data.checkOut,
      bedrooms: data.bedrooms,
      adults: data.adults,
      children: data.children,
      babies: data.babies,
      message: data.message ?? "",
      status: "pending",
      early_check_in: data.earlyCheckIn,
      late_check_out: data.lateCheckOut,
      preferred_language: data.lang ?? null,
      quoted_rental_cents: data.quotedRentalCents ?? null,
      quoted_extras_cents: data.quotedExtrasCents ?? null,
      quoted_total_cents: data.quotedTotalCents ?? null,
      quoted_discount_cents: data.quotedDiscountCents ?? null,
      quoted_currency: data.quotedCurrency ?? null,
      quoted_breakdown: data.quotedBreakdown
        ? (() => { try { return JSON.parse(data.quotedBreakdown as string); } catch { return null; } })()
        : null,
    }).select("id").single();

    if (insertErr) {
      console.error("[submitBookingInquiry] insert failed", insertErr);
      throw new Error("Could not save your inquiry. Please try again.");
    }

    // Create the guest-info form record + fill-in token.
    let guestInfoUrl: string | undefined;
    try {
      const bytes = new Uint8Array(24);
      crypto.getRandomValues(bytes);
      const guestInfoToken = Array.from(bytes)
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
      let renterFirst = (data.firstName ?? "").trim();
      let renterFamily: string | null = (data.lastName ?? "").trim() || null;
      if (!renterFirst && !renterFamily) {
        const split = splitFullName(String(data.name ?? ""));
        renterFirst = split.first;
        renterFamily = split.family || null;
      }
      await supabaseAdmin.from("guest_info_submissions").insert({
        booking_id: inserted.id,
        token: guestInfoToken,
        renter_family_name: renterFamily,
        renter_first_name: renterFirst,
        renter_email: data.email,
        renter_phone: data.phone,
        renter_country: data.country,
        bedrooms_requested: data.bedrooms,
      });
      guestInfoUrl = `https://casamipajobe.com/api/public/g/${guestInfoToken}`;
    } catch (e) {
      console.error("[submitBookingInquiry] guest info row failed", e);
    }

    // Notify admins (fire-and-forget; errors are swallowed inside).
    try {
      const { notifyAdminsOfBookingEvent } = await import("./admin-notify.server");
      await notifyAdminsOfBookingEvent({
        eventType: "new",
        reference,
        guestName: data.name,
        guestEmail: data.email,
        guestPhone: data.phone,
        country: data.country,
        checkIn: data.checkIn,
        checkOut: data.checkOut,
        adults: data.adults,
        children: data.children,
        babies: data.babies,
        bedrooms: data.bedrooms,
        status: "pending",
        message: data.message ?? "",
      });
    } catch (e) {
      console.error("[submitBookingInquiry] admin notify failed", e);
    }

    // Create a Google Calendar event (fire-and-forget).
    try {
      const { upsertBookingEvent } = await import("./gcal.server");
      await upsertBookingEvent({
        bookingId: inserted.id,
        existingEventId: null,
        booking: {
          reference,
          guestName: data.name,
          guestEmail: data.email,
          guestPhone: data.phone,
          country: data.country,
          checkIn: data.checkIn,
          checkOut: data.checkOut,
          adults: data.adults,
          children: data.children,
          babies: data.babies,
          bedrooms: data.bedrooms,
          status: "pending",
          message: data.message ?? "",
        },
      });
    } catch (e) {
      console.error("[submitBookingInquiry] gcal sync failed", e);
    }

    // Thank-you email to the guest, plus a SEPARATE mail with the guest-info form.
    // Both in the renter's selected site language.
    try {
      const check = await validateRenterEmail(data.email);
      if (!check.ok) {
        console.warn("[submitBookingInquiry] skipping guest thank-you", {
          reference,
          reason: check.reason,
        });
      } else {
        const { sendTransactionalEmail } = await import("./email-sender.server");
        const lang = (data.lang ?? "nl") as string;
        let bd: any = null;
        try {
          bd = data.quotedBreakdown ? JSON.parse(data.quotedBreakdown as string) : null;
        } catch {
          bd = null;
        }
        await sendTransactionalEmail({
          templateName: "guest-inquiry-received",
          recipientEmail: check.email,
          idempotencyKey: `guest-inquiry-received-${reference}`,
          templateData: {
            guestName: data.name,
            reference,
            checkIn: data.checkIn,
            checkOut: data.checkOut,
            adults: data.adults,
            children: data.children,
            babies: data.babies,
            bedrooms: data.bedrooms,
            lang,
            nights: bd?.nights ?? null,
            quotedRentalCents: data.quotedRentalCents ?? null,
            quotedCleaningCents: bd?.cleaning != null ? Math.round(bd.cleaning * 100) : null,
            quotedLinenCents: bd?.linenTotal != null ? Math.round(bd.linenTotal * 100) : null,
            quotedBabyPackCents:
              bd?.babyPackTotal != null ? Math.round(bd.babyPackTotal * 100) : null,
            quotedExtrasCents: data.quotedExtrasCents ?? null,
            quotedDiscountCents: data.quotedDiscountCents ?? null,
            quotedDiscountLabel: bd?.discount?.label ?? null,
            quotedTotalCents: data.quotedTotalCents ?? null,
            quotedCurrency: data.quotedCurrency ?? "eur",
          },
        });

        // Aparte mail met de gastinformatiefiche.
        if (guestInfoUrl) {
          const localized = `${guestInfoUrl}?lang=${lang}`;
          await sendTransactionalEmail({
            templateName: "guest-info-reminder",
            recipientEmail: check.email,
            idempotencyKey: `guest-info-reminder-${reference}`,
            templateData: {
              guestName: data.name,
              reference,
              checkIn: data.checkIn,
              checkOut: data.checkOut,
              guestInfoUrl: localized,
              locale: lang,
            },
          }).catch((err) =>
            console.error("[submitBookingInquiry] guest-info mail failed", err),
          );
        }
      }
    } catch (e) {
      console.error("[submitBookingInquiry] guest thank-you failed", e);
    }

    // Admin booking confirmation (summary + reference) to all admins.
    try {
      const { sendTransactionalEmail } = await import("./email-sender.server");
      const { ADMIN_NOTIFY_RECIPIENTS } = await import("./admin-notify.server");
      const nights = Math.max(
        1,
        Math.round(
          (new Date(data.checkOut).getTime() - new Date(data.checkIn).getTime()) /
            (1000 * 60 * 60 * 24),
        ),
      );
      const totalGuests = (data.adults ?? 0) + (data.children ?? 0) + (data.babies ?? 0);
      const quotedTotalFormatted =
        data.quotedTotalCents != null
          ? new Intl.NumberFormat("nl-BE", {
              style: "currency",
              currency: data.quotedCurrency ?? "EUR",
            }).format((data.quotedTotalCents as number) / 100)
          : undefined;
      const templateData = {
        reference,
        guestName: data.name,
        guestEmail: data.email,
        guestPhone: data.phone,
        country: data.country,
        checkIn: data.checkIn,
        checkOut: data.checkOut,
        nights,
        adults: data.adults,
        children: data.children,
        babies: data.babies,
        totalGuests,
        bedrooms: data.bedrooms,
        message: data.message ?? "",
        quotedTotalFormatted,
      };
      await Promise.all(
        ADMIN_NOTIFY_RECIPIENTS.map((recipient) =>
          sendTransactionalEmail({
            templateName: "booking-confirmation-admin",
            recipientEmail: recipient,
            idempotencyKey: `booking-confirmation-admin-${reference}-${recipient}`,
            templateData,
          }).catch((err) =>
            console.error("[submitBookingInquiry] admin confirmation failed", {
              recipient,
              error: err instanceof Error ? err.message : String(err),
            }),
          ),
        ),
      );
    } catch (e) {
      console.error("[submitBookingInquiry] admin confirmation block failed", e);
    }

    // NOTE: We intentionally do NOT send `booking-confirmation-guest` here.
    // The renter only receives the `guest-inquiry-received` mail above (with
    // the personalized guest-info link). A booking confirmation to the renter
    // is sent later when the deposit is received (deposit-received template).

    return {
      ok: true as const,
      conflict: false as const,
      reference,
      message:
        "Your inquiry has been received. We will reply within 24 hours.",
    };
  });